"use client";

import Image from "next/image";
import YouTubeVideo from "youtube-video-element/react";

export interface StoryVideoProps {
  /** Embed URL from `youtubeEmbedSrc`. */
  src: string;
  poster: string;
  /** Sizes for the poster, which fills the frame the clip plays in. */
  posterSizes: string;
  posterPriority?: boolean;
  title: string;
  autoPlay?: boolean;
  /** A frame of the film is on screen, so the poster comes off it. */
  started: boolean;
  /** Callback ref for the element `useVideoTransport` drives. */
  attach: (video: HTMLVideoElement | null) => void;
  onPress: () => void;
}

/**
 * Cancels the element's own `cc_load_policy: 1` default, which forces YouTube's
 * auto-generated captions on over a film that carries its own lower-thirds. Its
 * types admit only that value. 0 is not an off switch, only "ask the viewer's
 * preference", so `silenceCaptions` does the actual work.
 */
const EMBED_CONFIG = { cc_load_policy: 0 } as unknown as { cc_load_policy: 1 };

/**
 * How tall the embed is drawn against the frame it fills. YouTube anchors the
 * video title and its watermark to the top and bottom of the player, and shows
 * both on every start and seek; an embed that overhangs by a fifth at each end
 * puts them in the letterbox that extra height creates, where the frame clips
 * them. The film is untouched: the player still fits it to the embed's width,
 * so it lands on the frame at exactly the size it did before.
 */
const EMBED_OVERSCAN = "140%";

interface CaptionApi {
  setOption?: (module: string, option: string, value: unknown) => void;
}

/**
 * Turns the caption track off through the player itself, which is the only
 * thing that answers: this clip plays YouTube's auto-generated captions over a
 * film that already carries its own lower-thirds. The captions module does not
 * exist until playback has started, and a seek can bring the track back, so
 * this runs on every `playing` rather than once at ready.
 *
 * Guarded end to end: `api` is the element's own handle on the iframe player
 * and is not part of its published type, so a version that renames it costs the
 * viewer captions they can turn off themselves, not a broken player.
 */
function silenceCaptions(player: EventTarget) {
  const { api } = player as { api?: CaptionApi };
  api?.setOption?.("captions", "track", {});
}

/**
 * The clip itself: a YouTube embed with none of YouTube's own interface, under
 * this player's drawn controls. The element speaks the `HTMLVideoElement` API,
 * so `useVideoTransport` drives it exactly as it drove a `<video>`.
 *
 * Expects a `relative` parent at the clip's aspect ratio, and to be painted
 * under the frame's overlays: it renders the film, its poster and the press
 * target, and nothing else.
 */
export default function StoryVideo({
  src,
  poster,
  posterSizes,
  posterPriority,
  title,
  autoPlay,
  started,
  attach,
  onPress,
}: StoryVideoProps) {
  return (
    <>
      {/* The embed carries no interface of its own: without `controls` YouTube
          draws nothing over the film, and `inert` with `pointer-events-none`
          keeps the iframe out of the tab order and away from the pointer, so
          its hover overlays never appear and every press lands on the controls
          this player draws. What it still draws at the top and bottom on every
          start and seek is overhung past the frame and clipped here. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <YouTubeVideo
          ref={attach}
          src={src}
          title={title}
          autoplay={autoPlay}
          playsInline
          config={EMBED_CONFIG}
          onPlaying={(event) => {
            silenceCaptions(event.currentTarget);
          }}
          inert
          style={{ height: EMBED_OVERSCAN }}
          className="absolute top-1/2 left-0 block min-h-0 w-full min-w-0 -translate-y-1/2"
        />
      </div>

      {/* Our own poster rather than the embed's thumbnail: it is the frame the
          card and the OG card already use, and it covers the related-video
          screen YouTube paints over the last frame when a clip ends. */}
      {!started && (
        <Image
          src={poster}
          alt=""
          fill
          sizes={posterSizes}
          priority={posterPriority}
          className="pointer-events-none object-cover"
        />
      )}

      {/* Pressing the film pauses it, as it did with a native clip. Not a
          control in its own right: the transport bar and the centred disc are
          the labelled ones. */}
      <div aria-hidden className="absolute inset-0" onClick={onPress} />
    </>
  );
}
